// frontend/src/features/zones/components/ZoneFilterBar.tsx
import { ChevronDown, Search } from 'lucide-react';
import { useState } from 'react';
import { Checkbox } from '@/components/atoms/checkbox';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/atoms/popover';
import { COUNTRY_LABELS } from '@/features/zones/constants/markets';
import type { IZoneListFilter, ZoneCountry, ZoneCountryFilter } from '@/types/api';
import { THIN_SCROLLBAR } from '@/libs/utils/thinScrollbar';

interface ZoneFilterBarProps {
  filter: Omit<IZoneListFilter, 'page' | 'limit'>;
  onChange: (next: Omit<IZoneListFilter, 'page' | 'limit'>) => void;
  /** Distinct zone names from `GET /zones/filters`. */
  nameOptions: string[];
  /** Assigned owner ids from `GET /zones/filters`; the `unset` option is added here. */
  ownerOptions: string[];
  /** Occupied markets from `GET /zones/filters`; the `unset` option is added here. */
  countryOptions: ZoneCountry[];
}

type MultiAxis = 'names' | 'countries' | 'owner_ids';

/** The `unset` sentinel, shared by the country and owner axes. */
const UNSET = 'unset';
const OWNER_UNSET_LABEL = 'Unassigned';
const COUNTRY_UNSET_LABEL = 'Unset';

const AXIS_LABELS: Record<MultiAxis, string> = {
  names: 'Zone name',
  countries: 'Country',
  owner_ids: 'owner',
};

/** Noun the count uses once more than one value is selected on the axis. */
const AXIS_COUNT_NOUNS: Record<MultiAxis, string> = {
  names: 'zone names',
  countries: 'countries',
  owner_ids: 'owners',
};

const AXIS_SEARCH_LABELS: Record<MultiAxis, string> = {
  names: 'Search zone names',
  countries: 'Search countries',
  owner_ids: 'Search owners',
};

const triggerButtonClass =
  'inline-flex items-center gap-1 rounded-md border border-input bg-background px-3 py-1.5 text-sm hover:bg-accent';

interface MultiSelectOption {
  value: string;
  label: string;
}

interface MultiSelectFilterProps {
  axis: MultiAxis;
  options: MultiSelectOption[];
  selected: string[];
  onToggle: (value: string) => void;
}

interface OptionListProps extends Omit<MultiSelectFilterProps, 'axis'> {
  searchLabel: string;
}

// Owns the query so that closing the popover — which unmounts the content — drops it.
function OptionList({ options, selected, onToggle, searchLabel }: OptionListProps) {
  const [query, setQuery] = useState('');
  const needle = query.trim().toLowerCase();
  const visible = needle ? options.filter((o) => o.label.toLowerCase().includes(needle)) : options;

  return (
    <>
      <div className="relative mx-1 mb-2">
        <Search
          className="pointer-events-none absolute left-2 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground"
          aria-hidden="true"
        />
        <input
          type="text"
          value={query}
          onChange={(event) => setQuery(event.target.value)}
          aria-label={searchLabel}
          placeholder={searchLabel}
          className="h-8 w-full rounded-md border border-input bg-background pl-7 pr-2 text-sm outline-none placeholder:text-muted-foreground focus-visible:ring-2 focus-visible:ring-ring"
        />
      </div>
      {visible.length === 0 ? (
        <p className="px-2 py-1.5 text-sm text-muted-foreground">No match</p>
      ) : (
        <ul className={`flex max-h-72 flex-col gap-0.5 overflow-y-auto ${THIN_SCROLLBAR}`}>
          {visible.map((option) => (
            <li key={option.value}>
              <label className="flex w-full cursor-pointer items-center gap-2 rounded-md px-2 py-1.5 text-sm hover:bg-accent hover:text-accent-foreground">
                <Checkbox
                  checked={selected.includes(option.value)}
                  onCheckedChange={() => onToggle(option.value)}
                  aria-label={option.label}
                />
                <span className="truncate">{option.label}</span>
              </label>
            </li>
          ))}
        </ul>
      )}
    </>
  );
}

function selectionLabel(axis: MultiAxis, selected: string[], options: MultiSelectOption[]): string {
  if (selected.length === 0) return 'All';
  if (selected.length > 1) return `${selected.length} ${AXIS_COUNT_NOUNS[axis]}`;
  return options.find((o) => o.value === selected[0])?.label ?? selected[0];
}

function MultiSelectFilter({ axis, options, selected, onToggle }: MultiSelectFilterProps) {
  const label = AXIS_LABELS[axis];
  const triggerLabel = selectionLabel(axis, selected, options);
  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          type="button"
          aria-label={`${label}: ${triggerLabel}`}
          className={triggerButtonClass}
        >
          <span className="text-muted-foreground">{label}:</span>
          <span className="font-medium">{triggerLabel}</span>
          <ChevronDown className="h-3.5 w-3.5" />
        </button>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        sideOffset={8}
        className="w-56 rounded-[10px] border-border p-2 shadow-modal"
      >
        <OptionList
          options={options}
          selected={selected}
          onToggle={onToggle}
          searchLabel={AXIS_SEARCH_LABELS[axis]}
        />
      </PopoverContent>
    </Popover>
  );
}

export function ZoneFilterBar({
  filter,
  onChange,
  nameOptions,
  ownerOptions,
  countryOptions,
}: ZoneFilterBarProps) {
  const names = filter.names ?? [];
  const countries = filter.countries ?? [];
  const owners = filter.owner_ids ?? [];

  function toggle(axis: MultiAxis, value: string) {
    const current: string[] = filter[axis] ?? [];
    const nextValues = current.includes(value)
      ? current.filter((v) => v !== value)
      : [...current, value];
    const next: Omit<IZoneListFilter, 'page' | 'limit'> = { ...filter };
    if (nextValues.length === 0) delete next[axis];
    else if (axis === 'countries') next.countries = nextValues as ZoneCountryFilter[];
    else next[axis] = nextValues;
    onChange(next);
  }

  // A selected value absent from the payload (its last zone changed mid-session, or
  // the request failed) must still be offered, or the BD cannot untick it here.
  function toOptions(
    values: string[],
    selectedValues: string[],
    labelOf: (value: string) => string = (value) => value,
  ): MultiSelectOption[] {
    const merged = [...values, ...selectedValues.filter((v) => !values.includes(v))];
    return merged.map((value) => ({ value, label: labelOf(value) }));
  }

  const nameSelectOptions = toOptions(nameOptions, names);
  const countrySelectOptions = toOptions([UNSET, ...countryOptions], countries, (code) =>
    code === UNSET ? COUNTRY_UNSET_LABEL : COUNTRY_LABELS[code as ZoneCountry],
  );
  const ownerSelectOptions = toOptions(
    [UNSET, ...ownerOptions.filter((id) => id !== UNSET)],
    owners,
    (id) => (id === UNSET ? OWNER_UNSET_LABEL : id),
  );

  const axes: { axis: MultiAxis; selected: string[]; options: MultiSelectOption[] }[] = [
    { axis: 'names', selected: names, options: nameSelectOptions },
    { axis: 'countries', selected: countries, options: countrySelectOptions },
    { axis: 'owner_ids', selected: owners, options: ownerSelectOptions },
  ];

  const hasActiveFilter = axes.some(({ selected }) => selected.length > 0);

  return (
    <div className="flex flex-wrap items-center gap-2">
      {axes.map(({ axis, selected, options }) => (
        <MultiSelectFilter
          key={axis}
          axis={axis}
          options={options}
          selected={selected}
          onToggle={(value) => toggle(axis, value)}
        />
      ))}

      {hasActiveFilter && (
        <button
          type="button"
          onClick={() => onChange({})}
          aria-label="Clear all filters"
          className="text-xs text-link underline underline-offset-2"
        >
          Clear all
        </button>
      )}
    </div>
  );
}
